<template>
  <div class="contact-container">
    <div
      v-if="message"
      class="contact-message"
    >
      {{ message }}
    </div>
    <div
      v-if="hasBadgeNumberInput"
      class="badge-number-section"
    >
      <div
        id="scanner-container"
        style="width: 300px; display: none;"
      >
        <div id="reader" />
      </div>
      <v-btn
        id="start-scanner-button"
        :loading="isLoadingScanner"
        @click="startScanner"
      >
        Scan Visitor Badge
      </v-btn>
    </div>
    <div
      v-once
      id="hubspot-form-container"
    />
  </div>
</template>

<script>

export default {
  name: 'HubspotContactForm',
  props: {
    formId: { type: String, required: true },
    portalId: { type: String, required: true },
    region: { type: String, default: 'eu1' },
    message: { type: String, default: '' },
    hasBadgeNumberInput: { type: Boolean, default: false },
    tradeShowName: { type: String, default: '' },
    formDataConstructor: { type: Function, default: null },
  },
  data() {
    return {
      html5QrCode: null,
      isLoading: true,
      isLoadingScanner: false,
    };
  },
  mounted() {
    if (this.hasBadgeNumberInput) {
      // If the form includes a badge number input, we can show the QR code scanner
      // to allow users to easily fill it in by scanning their badge.
      // This is an optional enhancement and can be toggled via the `hasBadgeNumberInput` prop.
      this.initCodeScanner();
    } else {
      // If there's no badge number input, we can directly initialize the HubSpot form.
      this.initHubSpotForm();
    }
  },
  beforeDestroy() {
    // Always clean up to avoid duplicate listeners
    window.removeEventListener('message', this.onHubSpotMessage);
  },
  methods: {
    initHubSpotForm() {
      // 1. Create and inject the HubSpot script
      const script = document.createElement('script');
      script.src = 'https://js.hsforms.net/forms/v2.js';
      script.async = true;
      // 2. Initialize the form once the script has loaded
      script.addEventListener('load', () => {
        this.isLoading = false;
        if (window.hbspt) {
          window.hbspt.forms.create({
            region: this.region, // e.g., "na1", "eu1" (check your HubSpot embed code)
            portalId: this.portalId,
            formId: this.formId,
            target: '#hubspot-form-container',
          });
        }
      });

      document.body.appendChild(script);

      // Listen for the HubSpot form submission event
      window.addEventListener('message', this.onHubSpotMessage);
    },
    initCodeScanner() {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/html5-qrcode';
      script.async = true;
      script.addEventListener('load', () => {
        this.isLoading = false;
      });
      document.body.appendChild(script);
    },
    startScanner() {
      this.isLoadingScanner = true;
      document.getElementById('scanner-container').style.display = 'block';

      this.html5QrCode = new window.Html5Qrcode('reader');
      const config = { fps: 10, qrbox: { width: 250, height: 250 } };

      this.html5QrCode.start({ facingMode: 'environment' }, config, this.onScanSuccess)
        .then(() => {
          document.getElementById('start-scanner-button').style.display = 'none';
          this.isLoadingScanner = false;
        })
        .catch((err) => {
          console.error('Camera error: ', err);
        });
    },
    onScanSuccess(decodedText, decodedResult) {
      console.log(`Code scanned: ${decodedText}`, decodedResult);
      // Stop the scanner once a code is read
      this.html5QrCode.stop().then(() => {
        document.getElementById('scanner-container').style.display = 'none';

        // 6. Pass data to HubSpot
        this.$router.replace({
          query: {
            ...this.$route.query,
            badge_number: decodedText,
          },
        }).catch(() => {
          // Ignore navigation errors caused by replacing the same route
        });
        this.autoSubmitToHubSpot(decodedText);
        // this.initHubSpotForm();
        // this.populateHubSpotField(decodedText);
      });
    },
    autoSubmitToHubSpot(badgeNumber) {
      // HubSpot Submission API endpoint
      const url = `https://api.hsforms.com/submissions/v3/integration/submit/${this.portalId}/${this.formId}`;

      const data = {
        fields: [
          // { name: 'badge_number', value: badgeNumber },
          { name: 'TICKET.subject', value: badgeNumber },
          { name: 'TICKET.content', value: this.tradeShowName },
          // Add any other known fields here if needed
          ...(typeof this.formDataConstructor === 'function' ? this.formDataConstructor(badgeNumber) : []),
        ],
      };

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
        .then((response) => {
          if (response.ok) {
            this.$emit('submitted', { badgeNumber });
          }
        })
        .catch((error) => console.error('Error:', error));
    },
    onHubSpotMessage(event) {
      // Ignore messages not from HubSpot
      // console.log('Received message event:', event);
      if (!event.data || event.data.type !== 'hsFormCallback') return;

      if (event.data.eventName === 'onFormSubmitted') {
        const { data } = event.data;

        const submittedData = data.submissionValues;

        this.$emit('submitted', submittedData);
      }
    },
  },
};

</script>

<style scoped>
/* Add any specific styling for your form container here */
#hubspotFormWrapper {
  min-height: 400px; /* Prevents layout shift while form loads */
}
.contact-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 6px;
}
.contact-message {
  margin-bottom: 20px;
  font-size: 16px;
  text-align: center;
}
.badge-number-section {
  height: 50vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
